import { useEffect, useState, useMemo } from 'react'
import { fetchLoginImages } from '../api/loginImages'
import defaultBgImage from '../images/login-desktop-bg.png'
import { useMediaQuery } from './useMediaQuery'
import { shuffleArray, loadImage } from '../utils/helpers'
import { safeStorage } from '../utils/helpers'

// local storage keys
const LS_ORDER = 'loginBg.order.v1' // order of images
const LS_INDEX = 'loginBg.index.v1' // index of the image
const LS_FP = 'loginBg.fp.v1' // fingerprint of the images
const LS_PREF = 'loginBg.prefetched.v1' // if the images are prefetched
const LS_LAST = 'loginBg.lastUrl.v1' // last shown image url

const fingerprint = (list) => list.slice().sort().join('||')

// get the next image before the first render
function getInitialUrlSync(isDesktop, reuseLast) {
  if (!isDesktop) return ''
  try {
    if (reuseLast) {
      const last = safeStorage.get(LS_LAST)
      if (last) return last
    }
    const order = JSON.parse(safeStorage.get(LS_ORDER) || '[]')
    let index = parseInt(safeStorage.get(LS_INDEX) || '0', 10) || 0

    if (Array.isArray(order) && order.length) {
      const url = order[index % order.length]
      const nextIndex = (index + 1) % order.length

      safeStorage.set(LS_INDEX, String(nextIndex))
      if (url) {
        safeStorage.set(LS_LAST, url)
      }

      return url || ''
    }
  } catch (error) {
    console.error('Error getting initial URL:', error)
  }
  return ''
}

// prefetch all images
async function prefetchAllAsync(urls, signal) {
  const tasks = urls.map((url) => loadImage(url, signal).catch(() => {}))
  await Promise.all(tasks)
}

// main hook
export function useLoginBackground(options = {}) {
  const { reuseLast = false } = options
  const isDesktop = useMediaQuery('(min-width: 992px)')

  const initialUrl = useMemo(() => getInitialUrlSync(isDesktop, reuseLast), [isDesktop, reuseLast])
  const [backgroundImage, setBackgroundImage] = useState(() => initialUrl)
  const [loading, setLoading] = useState(() => !initialUrl)

  useEffect(() => {
    const controller = new AbortController()
    let cancelled = false

    if (!isDesktop) return

    if (initialUrl) {
      setBackgroundImage(initialUrl)
      setLoading(false)
    } else {
      setBackgroundImage('')
      setLoading(true)
    }

    (async () => {
      try {
        const response = await fetchLoginImages(controller.signal)
        const raw = Array.isArray(response?.data) ? response.data : []
        const list = Array.from(new Set(raw.filter(Boolean)))

        if (!response?.success || list.length === 0) {
          if (!initialUrl) {
            setBackgroundImage(defaultBgImage)
            safeStorage.set(LS_LAST, defaultBgImage)
            setLoading(false)
          }
          return
        }

        // check and create order
        const fp = fingerprint(list)
        const storedFp = safeStorage.get(LS_FP)
        let order = []

        if (storedFp !== fp) {
          // if the set has changed, shuffle the list
          order = shuffleArray(list)
          safeStorage.set(LS_ORDER, JSON.stringify(order))
          safeStorage.set(LS_INDEX, '0')
          safeStorage.set(LS_FP, fp)
          safeStorage.remove(LS_PREF)
        } else {
          try {
            const parsed = JSON.parse(safeStorage.get(LS_ORDER) || '[]')
            const ok = Array.isArray(parsed) && parsed.length === list.length && parsed.every((url) => list.includes(url))
            order = ok ? parsed : shuffleArray(list)
            if (!ok) {
              safeStorage.set(LS_ORDER, JSON.stringify(order))
              safeStorage.set(LS_INDEX, '0')
            }
          } catch {
            order = shuffleArray(list)
            safeStorage.set(LS_ORDER, JSON.stringify(order))
            safeStorage.set(LS_INDEX, '0')
          }
        }

        // prefetch all images if not prefetched yet
        if (!safeStorage.get(LS_PREF)) {
          await prefetchAllAsync(order, controller.signal)
          if (!controller.signal.aborted) {
            safeStorage.set(LS_PREF, '1')
          }
        }

        if (!initialUrl) {
          // take the first from the order, wait for it, then show it and shift the index
          const index = 0
          const firstImage = order[index]

          await loadImage(firstImage, controller.signal)

          if (cancelled || controller.signal.aborted) return
          setBackgroundImage(firstImage)
          
          safeStorage.set(LS_LAST, firstImage)
          setLoading(false)
          safeStorage.set(LS_INDEX, String((index + 1) % order.length))
        }
      } catch (e) {
        if (!cancelled && !controller.signal.aborted) {
          console.error('useLoginBackground error:', e)
          if (!initialUrl) {
            setBackgroundImage(defaultBgImage)
            safeStorage.set(LS_LAST, defaultBgImage)

          }
          setLoading(false)
        }
      }
    })()

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [isDesktop, initialUrl])

  // persist last on changes
  useEffect(() => {
    if (backgroundImage) safeStorage.set(LS_LAST, backgroundImage)
  }, [backgroundImage])

  return { url: backgroundImage, loading }
}
